import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { INVESTMENT_TYPE_LABELS } from '../investments/investmentTableConstants';
import { useScopedPerformance } from './useScopedPerformance';
import { formatCurrency } from './performanceUtils';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';

const SCOPE_OPTIONS = [
  { value: 'portfolio', label: 'Whole portfolio' },
  { value: 'asset_class', label: 'By asset class' },
  { value: 'asset_type', label: 'By investment type' },
  { value: 'investment', label: 'Specific investment' },
];

// Shared 5-option date-range list for the Performance tab.
// Exported so the Compare card can reuse the same labels.
export const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '1m', label: 'Last 1 month' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '1y', label: 'Last 1 year' },
];

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

function ScopedTooltipContent({ active, payload, label, valueFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg text-sm">
      <div className="text-slate-600 mb-1">{fmtDate(label)}</div>
      <div className="font-mono font-bold text-emerald-700">{valueFormatter(payload[0].value)}</div>
    </div>
  );
}

const ScopedPerformanceChart = () => {
  const sp = useScopedPerformance();
  const { scope, setScope, scopeValue, setScopeValue, currency, setCurrency, period, setPeriod, range, setRange, scopes, data, meta, isLoading, error } = sp;

  // Reset scopeValue to 'all' whenever scope changes — prevents stale ids leaking into a different dropdown.
  React.useEffect(() => { setScopeValue('all'); }, [scope, setScopeValue]);

  const valueOptions = (() => {
    if (scope === 'asset_class') return scopes.asset_classes;
    if (scope === 'asset_type') return scopes.asset_types.map(t => ({ value: t, label: INVESTMENT_TYPE_LABELS[t] || t }));
    if (scope === 'investment') return scopes.investments.map(inv => ({ value: inv.investment_id, label: `${inv.name} (${INVESTMENT_TYPE_LABELS[inv.type] || inv.type})` }));
    return [];
  })();

  const showCurrencyToggle = scope === 'investment' && scopeValue !== 'all';
  const investmentNativeCurrency = (scope === 'investment')
    ? (scopes.investments.find(i => i.investment_id === scopeValue)?.currency_native || '')
    : '';

  const formatVal = (v) => {
    if (scope === 'investment' && currency === 'native' && investmentNativeCurrency) {
      return `${investmentNativeCurrency} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    return formatCurrency(v);
  };

  const titleText = (() => {
    if (scope === 'portfolio') return 'Portfolio Value Over Time';
    const label = valueOptions.find(o => o.value === scopeValue)?.label;
    if (!label) return 'Performance';
    if (scope === 'asset_class') return `${label} — Value Over Time`;
    if (scope === 'asset_type') return `${label} — Value Over Time`;
    return `${label} — Value Over Time`;
  })();

  const empty = !isLoading && data.length === 0;
  const earliest = data[0]?.date;
  const helpCopy = (() => {
    if (scope === 'portfolio') return 'Whole-portfolio history captured by the snapshot job.';
    if (data.length > 0) return `Tracking started ${fmtDate(earliest)}. New snapshots add a new point each time you press Update Portfolio Value.`;
    return 'Per-investment tracking starts from your next portfolio update. Press Update Portfolio Value above to record the first data point.';
  })();

  return (
    <Card className="portfolio-card" data-testid="scoped-performance-card">
      <CardHeader className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base sm:text-xl font-serif" data-testid="scoped-performance-title">{titleText}</CardTitle>
            <p className="text-xs text-slate-500 mt-1">{helpCopy}</p>
          </div>
          <div className="text-xs text-slate-500" data-testid="scoped-performance-period-badge">{period} • {data.length} {data.length === 1 ? 'point' : 'points'}</div>
        </div>

        {/* Controls row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="scoped-performance-scope">Performance scope</label>
            <select
              id="scoped-performance-scope"
              data-testid="scoped-performance-scope-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              {SCOPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          {scope !== 'portfolio' && (
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="scoped-performance-value">
                {scope === 'asset_class' ? 'Asset class' : scope === 'asset_type' ? 'Investment type' : 'Investment'}
              </label>
              <select
                id="scoped-performance-value"
                data-testid="scoped-performance-value-select"
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
                value={scopeValue}
                onChange={(e) => setScopeValue(e.target.value)}
              >
                <option value="all">— Select —</option>
                {valueOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          )}

          {showCurrencyToggle && (
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="scoped-performance-currency">Currency</label>
              <select
                id="scoped-performance-currency"
                data-testid="scoped-performance-currency-select"
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="home">Home currency</option>
                <option value="native">Native currency{investmentNativeCurrency ? ` (${investmentNativeCurrency})` : ''}</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="scoped-performance-range">Period</label>
            <select
              id="scoped-performance-range"
              data-testid="scoped-performance-range-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={range}
              onChange={(e) => setRange(e.target.value)}
            >
              {RANGE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="scoped-performance-period">Aggregation</label>
            <select
              id="scoped-performance-period"
              data-testid="scoped-performance-period-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="MONTHLY">Monthly</option>
              <option value="QUARTERLY">Quarterly</option>
              <option value="ANNUALLY">Annually</option>
            </select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {scope !== 'portfolio' && scopeValue === 'all' ? (
          <div className="text-center py-12 text-slate-500" data-testid="scoped-performance-prompt">
            <TrendingUp className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">Pick {scope === 'asset_class' ? 'an asset class' : scope === 'asset_type' ? 'an investment type' : 'an investment'} above to see its value history.</p>
          </div>
        ) : isLoading ? (
          <div className="h-64 bg-slate-100 animate-pulse rounded" data-testid="scoped-performance-loading" />
        ) : error ? (
          <div className="text-center py-10 text-rose-600 flex flex-col items-center gap-2" data-testid="scoped-performance-error">
            <AlertCircle className="w-6 h-6" />
            <p className="text-sm">Could not load performance data. Please retry.</p>
          </div>
        ) : empty ? (
          <div className="text-center py-12 text-slate-500" data-testid="scoped-performance-empty">
            <TrendingUp className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">No data yet</p>
            <p className="text-xs mt-1 max-w-md mx-auto">{helpCopy}</p>
          </div>
        ) : (
          <div className="h-64" data-testid="scoped-performance-chart" data-sensitive="chart">
            <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
              <LineChart data={data} margin={{ top: 5, right: 12, left: 12, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#64748B" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatVal(v).replace(/\.\d+/, '')} stroke="#64748B" tick={{ fontSize: 11 }} width={75} />
                <Tooltip content={<ScopedTooltipContent valueFormatter={formatVal} />} />
                <Line type="monotone" dataKey="current_value" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScopedPerformanceChart;
